import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../../App';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { COUNTRY_CODES } from '../../data/countryCodes';
import SendEmailModal from '../shared/SendEmailModal';
import OrganizerContracts from '../../pages/OrganizerContracts';
import {
  Plus, Search, Edit, Trash2, ShieldBan, ShieldCheck,
  Clock, Star, AlertTriangle, Building2, Phone, Mail, MapPin,
  UserPlus, User, Receipt, Check, ChevronsUpDown, Globe, Eye,
  FileText, ChevronRight, ChevronLeft, CheckCircle2, Inbox, RefreshCw, Send, ArrowDown, ArrowUp, Paperclip, X
} from 'lucide-react';

const uuidv4 = () => crypto.randomUUID();

// ── Process (Süreç) badge config ──────────────────────────────────────────────
const PROCESS_CONFIG = {
  'Rezerve':          { bg: 'bg-orange-500', ring: 'ring-orange-300', label: 'Rezerve' },
  'Satıldı':          { bg: 'bg-red-500',    ring: 'ring-red-300',    label: 'Satıldı' },
  'Sözleşme Yapıldı': { bg: 'bg-teal-500',   ring: 'ring-teal-300',   label: 'Sözleşme Yapıldı' },
};
const PROCESS_ORDER = ['Rezerve', 'Satıldı', 'Sözleşme Yapıldı'];

function ProcessBadges({ companyId, standSummary }) {
  const activities = (standSummary && standSummary[companyId]) || [];
  if (activities.length === 0) return <span className="text-gray-300 text-xs">—</span>;

  const grouped = {};
  activities.forEach(act => {
    if (!grouped[act.display_status]) grouped[act.display_status] = [];
    grouped[act.display_status].push(act);
  });

  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex items-center gap-1.5 flex-wrap">
        {PROCESS_ORDER.map(status => {
          const items = grouped[status];
          if (!items || items.length === 0) return null;
          const cfg = PROCESS_CONFIG[status];
          return (
            <Tooltip key={status}>
              <TooltipTrigger asChild>
                <div
                  className={`w-6 h-6 rounded-full ${cfg.bg} text-white flex items-center justify-center text-xs font-bold cursor-default select-none ring-2 ${cfg.ring} ring-offset-1`}
                  data-testid={`process-badge-${companyId}-${status.replace(/\s/g, '-')}`}
                >
                  {items.length}
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-white border border-gray-200 text-gray-700 shadow-xl p-0 rounded-lg max-w-xs">
                <div className="p-2 text-xs">
                  <p className="font-semibold text-gray-700 mb-1 border-b pb-1">{cfg.label}</p>
                  {items.map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5 py-0.5 whitespace-nowrap text-gray-600">
                      <span className={`w-1.5 h-1.5 rounded-full ${cfg.bg} shrink-0`} />
                      <span className="font-medium text-gray-800">{item.exhibition_name}</span>
                      <span className="text-gray-400">·</span>
                      <span>{item.area} m²</span>
                      <span className="text-gray-400">·</span>
                      <span>{item.stand_number}</span>
                    </div>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

const EMPTY_CONTACT = {
  id: '', name: '', title: '',
  email: '',
  phone_country_code: '+90', phone_number: '',
  country: '', city: '', zip_code: '',
  is_signatory: false,
};
const EMPTY_FORM = {
  name: '', full_name: '', email: '', phone: '', address: '',
  country: '', city: '', zip_code: '',
  notes: '', tax_office: '', tax_number: '', contacts: [],
};

// ── Country Select Component ──────────────────────────────────────────────────
function CountrySelect({ value, onChange, placeholder = 'Ülke seçin...', testId }) {
  const [open, setOpen] = useState(false);
  const selected = COUNTRY_CODES.find(c => c.name === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal h-9 text-sm"
          data-testid={testId}
        >
          <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
            {selected ? `${selected.flag} ${selected.name}` : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Ülke ara..." className="h-9" />
          <CommandList>
            <CommandEmpty>Bulunamadı</CommandEmpty>
            <CommandGroup>
              {COUNTRY_CODES.map(c => (
                <CommandItem
                  key={c.code + c.name}
                  value={c.name}
                  onSelect={() => { onChange(c.name); setOpen(false); }}
                  className="text-sm"
                >
                  <span className="mr-2">{c.flag}</span>
                  {c.name}
                  {value === c.name && <Check className="ml-auto h-3.5 w-3.5" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── Phone Country Code Select ─────────────────────────────────────────────────
function PhoneCodeSelect({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const selected = COUNTRY_CODES.find(c => c.code === value) || { flag: '🌍', code: value };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-28 justify-between font-normal h-8 text-xs shrink-0 px-2"
          data-testid={testId}
        >
          <span>{selected.flag} {selected.code}</span>
          <ChevronsUpDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Ülke ara..." className="h-9" />
          <CommandList>
            <CommandEmpty>Bulunamadı</CommandEmpty>
            <CommandGroup>
              {COUNTRY_CODES.map(c => (
                <CommandItem
                  key={c.code + c.name}
                  value={`${c.name} ${c.code}`}
                  onSelect={() => { onChange(c.code); setOpen(false); }}
                  className="text-sm"
                >
                  <span className="mr-2">{c.flag}</span>
                  {c.name} <span className="text-gray-400 ml-1">{c.code}</span>
                  {value === c.code && <Check className="ml-auto h-3.5 w-3.5" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── Title Case helper (Turkish) ───────────────────────────────────────────────
const toTitleCase = (str) => {
  if (!str) return str;
  return str.charAt(0).toLocaleUpperCase('tr') + str.slice(1).toLocaleLowerCase('tr');
};

export default function OrganizerCompanyList() {
  const { user } = useContext(AuthContext);
  const { filter: urlFilter } = useParams();
  const navigate = useNavigate();

  const filterMap = { 'favorite': 'favorite', 'passive': 'passive', 'blacklist': 'blacklisted' };
  const initialTab = filterMap[urlFilter] || 'all';

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [standSummary, setStandSummary] = useState({});
  const [uncontractedFlags, setUncontractedFlags] = useState({});

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [blacklistTarget, setBlacklistTarget] = useState(null);
  const [blacklistReason, setBlacklistReason] = useState('');
  const [previewCompany, setPreviewCompany] = useState(null);

  // Create Contract Modal
  const [createContractCompany, setCreateContractCompany] = useState(null);
  const [contractModalStep, setContractModalStep] = useState(1); // 1, 2, 'success'
  const [contractAssignments, setContractAssignments] = useState([]);
  const [contractTemplates, setContractTemplates] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [contractModalLoading, setContractModalLoading] = useState(false);

  // Email Modal
  const [emailTarget, setEmailTarget] = useState(null); // { id, name } company

  // Mailleşmeler tab state
  const [selectedMailCompany, setSelectedMailCompany] = useState(null);
  const [companyEmails, setCompanyEmails] = useState([]);
  const [inboxSummary, setInboxSummary] = useState({});
  const [mailsLoading, setMailsLoading] = useState(false);
  const [fetchingInbox, setFetchingInbox] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [mailContext, setMailContext] = useState(null); // { contracts: [], stands: [] }
  const [mailFilter, setMailFilter] = useState('all'); // 'all' | 'contract:{id}' | 'stand:{id}'
  const [replyTarget, setReplyTarget] = useState(null); // { toEmail, subject, messageId, contractId, standId }
  const [replyBody, setReplyBody] = useState('');
  const [replySending, setReplySending] = useState(false);

  useEffect(() => {
    fetchCompanies(activeTab);
    fetchStandSummary();
    fetchUncontractedFlags();
    fetchInboxSummary();
  }, [activeTab]); // eslint-disable-line

  const token = () => localStorage.getItem('token');

  const fetchStandSummary = async () => {
    try {
      const res = await axios.get(`${API}/companies/stand-summary`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setStandSummary(res.data || {});
    } catch {
      // non-critical, silently ignore
    }
  };

  const fetchUncontractedFlags = async () => {
    try {
      const res = await axios.get(`${API}/companies/uncontracted-flags`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setUncontractedFlags(res.data || {});
    } catch {
      // non-critical
    }
  };

  const fetchInboxSummary = async () => {
    try {
      const res = await axios.get(`${API}/emails/inbox-summary`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setInboxSummary(res.data || {});
    } catch {
      // non-critical
    }
  };

  const [replyFiles, setReplyFiles] = useState([]);
  const replyFileInputRef = React.useRef(null);
  const REPLY_MAX_FILES = 5;
  const REPLY_MAX_MB = 50;

  const handleReplyFileAdd = (e) => {
    const files = Array.from(e.target.files || []);
    const current = [...replyFiles];
    let totalBytes = current.reduce((s, f) => s + f.size, 0);
    for (const f of files) {
      if (current.length >= REPLY_MAX_FILES) { toast.error(`En fazla ${REPLY_MAX_FILES} dosya ekleyebilirsiniz`); break; }
      if (totalBytes + f.size > REPLY_MAX_MB * 1024 * 1024) { toast.error(`Toplam ek boyutu ${REPLY_MAX_MB}MB'ı geçemez`); break; }
      current.push(f);
      totalBytes += f.size;
    }
    setReplyFiles(current);
    if (replyFileInputRef.current) replyFileInputRef.current.value = '';
  };

  const sendReply = async () => {
    if (!replyBody.trim()) { toast.error('Yanıt içeriği boş olamaz'); return; }
    setReplySending(true);
    try {
      const formData = new FormData();
      formData.append('company_id', selectedMailCompany.id);
      if (replyTarget.contractId) formData.append('contract_id', replyTarget.contractId);
      if (replyTarget.standId) formData.append('stand_id', replyTarget.standId);
      formData.append('in_reply_to', replyTarget.messageId);
      formData.append('to_emails', JSON.stringify([replyTarget.toEmail]));
      formData.append('cc_emails', JSON.stringify([]));
      formData.append('bcc_emails', JSON.stringify([]));
      formData.append('subject', replyTarget.subject.startsWith('Re:') ? replyTarget.subject : `Re: ${replyTarget.subject}`);
      formData.append('body', replyBody);
      formData.append('attach_contract_pdf', 'false');
      replyFiles.forEach(f => formData.append('attachments', f));

      await axios.post(`${API}/emails/send`, formData, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success('Yanıt gönderildi!');
      setReplyTarget(null);
      setReplyBody('');
      setReplyFiles([]);
      await loadCompanyEmails(selectedMailCompany);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yanıt gönderilemedi');
    } finally {
      setReplySending(false);
    }
  };

  const loadCompanyEmails = async (company) => {
    setSelectedMailCompany(company);
    setMailsLoading(true);
    setCompanyEmails([]);
    setExpandedEmail(null);
    setMailFilter('all');
    setMailContext(null);
    try {
      const [emailRes, ctxRes] = await Promise.all([
        axios.get(`${API}/emails/company/${company.id}`, {
          headers: { Authorization: `Bearer ${token()}` }
        }),
        axios.get(`${API}/companies/${company.id}/email-context`, {
          headers: { Authorization: `Bearer ${token()}` }
        }),
      ]);

      // Backfill contract_id/stand_id on received emails from the matching sent email
      const rawEmails = emailRes.data || [];
      const sentMap = {};
      rawEmails.filter(e => e.direction === 'sent').forEach(e => {
        if (e.message_id) sentMap[e.message_id] = { contract_id: e.contract_id, stand_id: e.stand_id };
      });
      const enriched = rawEmails.map(e => {
        if (e.direction === 'received' && e.in_reply_to && sentMap[e.in_reply_to]) {
          const ref = sentMap[e.in_reply_to];
          return {
            ...e,
            contract_id: e.contract_id || ref.contract_id,
            stand_id: e.stand_id || ref.stand_id,
          };
        }
        return e;
      });

      setCompanyEmails(enriched);
      setMailContext(ctxRes.data || { contracts: [], stands: [] });
    } catch {
      toast.error('E-postalar yüklenemedi');
    } finally {
      setMailsLoading(false);
    }
  };

  const refreshInbox = async () => {
    setFetchingInbox(true);
    try {
      const res = await axios.post(`${API}/emails/fetch-inbox`, {}, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      toast.success(res.data.message);
      fetchInboxSummary();
      if (selectedMailCompany) loadCompanyEmails(selectedMailCompany);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gelen kutusu yenilenemedi');
    } finally {
      setFetchingInbox(false);
    }
  };

  const fetchCompanies = async (filter = 'all') => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/companies?filter=${filter}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setCompanies(res.data);
    } catch {
      toast.error('Firmalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.id}`, formData, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        toast.success('Firma güncellendi');
      } else {
        await axios.post(`${API}/companies`, formData, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        toast.success('Firma eklendi');
      }
      setFormOpen(false);
      setEditingCompany(null);
      setFormData(EMPTY_FORM);
      fetchCompanies(activeTab);
      fetchStandSummary();
      fetchUncontractedFlags();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/companies/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      toast.success('Firma silindi');
      setDeleteTarget(null);
      fetchCompanies(activeTab);
    } catch {
      toast.error('Silinemedi');
    }
  };

  const handleBlacklist = async () => {
    if (!blacklistTarget) return;
    try {
      if (blacklistTarget.is_blacklisted) {
        await axios.post(`${API}/companies/${blacklistTarget.id}/unblacklist`, {}, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        toast.success('Kara listeden çıkarıldı');
      } else {
        await axios.post(`${API}/companies/${blacklistTarget.id}/blacklist`,
          { reason: blacklistReason },
          { headers: { Authorization: `Bearer ${token()}` } }
        );
        toast.success('Kara listeye alındı');
      }
      setBlacklistTarget(null);
      setBlacklistReason('');
      fetchCompanies(activeTab);
    } catch {
      toast.error('İşlem başarısız');
    }
  };

  const openEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name || '',
      full_name: company.full_name || '',
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || '',
      country: company.country || '',
      city: company.city || '',
      zip_code: company.zip_code || '',
      notes: company.notes || '',
      tax_office: company.tax_office || '',
      tax_number: company.tax_number || '',
      contacts: company.contacts ? company.contacts.map(c => ({
        ...c,
        phone_country_code: c.phone_country_code || '+90',
        phone_number: c.phone_number || c.phone || '',
        country: c.country || '',
        city: c.city || '',
        zip_code: c.zip_code || '',
      })) : [],
    });
    setFormOpen(true);
  };

  const openCreate = () => {
    setEditingCompany(null);
    setFormData(EMPTY_FORM);
    setFormOpen(true);
  };

  const openCreateContract = async (company) => {
    setCreateContractCompany(company);
    setContractModalStep(1);
    setSelectedAssignment(null);
    setSelectedTemplate(null);
    setContractAssignments([]);
    setContractTemplates([]);
    setContractModalLoading(true);
    try {
      const [assignRes, tmplRes] = await Promise.all([
        axios.get(`${API}/companies/${company.id}/uncontracted-assignments`, {
          headers: { Authorization: `Bearer ${token()}` }
        }),
        axios.get(`${API}/contract-templates`, {
          headers: { Authorization: `Bearer ${token()}` }
        }),
      ]);
      setContractAssignments(assignRes.data);
      setContractTemplates(tmplRes.data);
    } catch {
      toast.error('Veriler yüklenemedi');
    } finally {
      setContractModalLoading(false);
    }
  };

  const handleCreateContract = async () => {
    if (!selectedAssignment || !selectedTemplate) return;
    setContractModalLoading(true);
    try {
      await axios.post(`${API}/contracts`, {
        stand_id: selectedAssignment.stand_id,
        template_id: selectedTemplate.id,
      }, { headers: { Authorization: `Bearer ${token()}` } });
      setContractModalStep('success');
      fetchStandSummary();
      fetchUncontractedFlags();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sözleşme oluşturulamadı');
    } finally {
      setContractModalLoading(false);
    }
  };

  const closeContractModal = () => {
    setCreateContractCompany(null);
    setContractModalStep(1);
    setSelectedAssignment(null);
    setSelectedTemplate(null);
  };

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const formatRevenue = (val) => {
    if (!val || val === 0) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(val);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const StatusBadge = ({ company }) => {
    if (company.is_blacklisted) return <Badge variant="destructive" className="text-xs">Kara Liste</Badge>;
    if (company.status === 'passive') return <Badge variant="secondary" className="text-xs">Pasif</Badge>;
    return <Badge className="bg-green-100 text-green-800 text-xs">Aktif</Badge>;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-teal-600" />
            Firma Yönetimi
          </h1>
          <p className="text-gray-500 text-sm mt-1">Firmalarınızı yönetin ve standlara atayın</p>
        </div>
        <Button id="new-company-trigger" onClick={openCreate} className="gap-2" data-testid="new-company-btn">
          <Plus className="w-4 h-4" />
          Yeni Firma
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Firma ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="company-search"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v)}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="all" className="gap-1" data-testid="tab-all">
            <Building2 className="w-3.5 h-3.5" />
            Tüm Firmalar
          </TabsTrigger>
          <TabsTrigger value="favorite" className="gap-1" data-testid="tab-favorite">
            <Star className="w-3.5 h-3.5" />
            Favori
          </TabsTrigger>
          <TabsTrigger value="passive" className="gap-1" data-testid="tab-passive">
            <Clock className="w-3.5 h-3.5" />
            Pasif
          </TabsTrigger>
          <TabsTrigger value="blacklisted" className="gap-1" data-testid="tab-blacklisted">
            <ShieldBan className="w-3.5 h-3.5" />
            Kara Liste
          </TabsTrigger>
          <TabsTrigger value="contracts" className="gap-1" data-testid="tab-contracts">
            <FileText className="w-3.5 h-3.5" />
            Sözleşmeler
          </TabsTrigger>
          <TabsTrigger value="maillesme" className="gap-1" data-testid="tab-maillesme">
            <Inbox className="w-3.5 h-3.5" />
            Mailleşmeler
          </TabsTrigger>
        </TabsList>

        {['all', 'favorite', 'passive', 'blacklisted'].map(tab => (
          <TabsContent key={tab} value={tab}>
            {loading ? (
              <div className="text-center py-8 text-gray-400">Yükleniyor...</div>
            ) : filtered.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-400">
                  {tab === 'favorite' && 'Henüz satış kaydı olan firma yok'}
                  {tab === 'passive' && 'Son 1 yılda tüm firmalar aktif'}
                  {tab === 'blacklisted' && 'Kara listede firma bulunmuyor'}
                  {tab === 'all' && (search ? 'Arama sonucu bulunamadı' : 'Henüz firma eklenmedi')}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Firma Adı</TableHead>
                      <TableHead>İletişim</TableHead>
                      {tab === 'favorite' && <TableHead className="text-right">Toplam Ciro</TableHead>}
                      {tab === 'passive' && <TableHead>Son Satış</TableHead>}
                      {tab === 'blacklisted' && <TableHead>Neden</TableHead>}
                      <TableHead>Süreç</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(company => (
                      <TableRow key={company.id} data-testid={`company-row-${company.id}`}>
                        <TableCell>
                          <div>
                            <div className="flex items-center gap-2">
                              {company.country && (() => {
                                const cc = COUNTRY_CODES.find(c => c.name === company.country);
                                return cc ? (
                                  <span title={company.country} className="text-base leading-none shrink-0">{cc.flag}</span>
                                ) : null;
                              })()}
                              <p className="font-semibold">{company.name}</p>
                            </div>
                            {(company.city || company.address) && (
                              <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                                <MapPin className="w-3 h-3" />
                                {[company.city, company.address].filter(Boolean).join(', ')}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            {company.email && (
                              <p className="text-xs flex items-center gap-1">
                                <Mail className="w-3 h-3 text-gray-400" /> {company.email}
                              </p>
                            )}
                            {company.phone && (
                              <p className="text-xs flex items-center gap-1">
                                <Phone className="w-3 h-3 text-gray-400" /> {company.phone}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        {tab === 'favorite' && (
                          <TableCell className="text-right font-semibold text-green-700">
                            {formatRevenue(company.total_revenue)}
                          </TableCell>
                        )}
                        {tab === 'passive' && (
                          <TableCell className="text-gray-500 text-sm">
                            {company.last_sale_at ? formatDate(company.last_sale_at) : 'Hiç satış yok'}
                          </TableCell>
                        )}
                        {tab === 'blacklisted' && (
                          <TableCell className="text-sm text-red-600">
                            {company.blacklist_reason || '-'}
                          </TableCell>
                        )}
                        <TableCell>
                          <ProcessBadges companyId={company.id} standSummary={standSummary} />
                        </TableCell>
                        <TableCell><StatusBadge company={company} /></TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            {uncontractedFlags[company.id] && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                                onClick={() => openCreateContract(company)}
                                data-testid={`create-contract-${company.id}`}
                                title="Sözleşme Oluştur"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                              onClick={() => setEmailTarget(company)}
                              data-testid={`send-email-company-${company.id}`}
                              title="E-posta Gönder"
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-gray-500 hover:text-gray-700"
                              onClick={() => setPreviewCompany(company)}
                              data-testid={`preview-company-${company.id}`}
                              title="Ön İzleme"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => openEdit(company)}
                              data-testid={`edit-company-${company.id}`}
                              title="Düzenle"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className={`h-8 w-8 ${company.is_blacklisted ? 'text-green-600 hover:text-green-700' : 'text-orange-500 hover:text-orange-600'}`}
                              onClick={() => { setBlacklistTarget(company); setBlacklistReason(''); }}
                              data-testid={`blacklist-company-${company.id}`}
                              title={company.is_blacklisted ? 'Kara listeden çıkar' : 'Kara listeye al'}
                            >
                              {company.is_blacklisted ? <ShieldCheck className="w-4 h-4" /> : <ShieldBan className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-500 hover:text-red-600"
                              onClick={() => setDeleteTarget(company)}
                              data-testid={`delete-company-${company.id}`}
                              title="Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
        ))}

        {/* Sözleşmeler Tab */}
        <TabsContent value="contracts">
          <OrganizerContracts embedded />
        </TabsContent>

        {/* Mailleşmeler Tab */}
        <TabsContent value="maillesme">
          <div className="flex gap-4 min-h-[500px]">
            {/* Sol: Firma Listesi */}
            <div className="w-72 shrink-0 border border-gray-200 rounded-xl overflow-hidden flex flex-col">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-700">Firmalar</p>
                <Button size="sm" variant="ghost" onClick={refreshInbox} disabled={fetchingInbox}
                  className="h-7 gap-1 text-xs text-gray-500" data-testid="refresh-inbox-btn">
                  <RefreshCw className={`w-3.5 h-3.5 ${fetchingInbox ? 'animate-spin' : ''}`} />
                  {fetchingInbox ? 'Yükleniyor...' : 'Yenile'}
                </Button>
              </div>
              <ScrollArea className="flex-1">
                {companies.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-6">Firma yok</p>
                ) : companies.map(company => {
                  const summary = inboxSummary[company.id] || {};
                  const isSelected = selectedMailCompany?.id === company.id;
                  return (
                    <button
                      key={company.id}
                      onClick={() => loadCompanyEmails(company)}
                      data-testid={`maillesme-company-${company.id}`}
                      className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${isSelected ? 'bg-teal-50 border-l-2 border-l-teal-500' : 'hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-800 truncate">{company.name}</span>
                        {summary.unread > 0 && (
                          <span className="ml-2 bg-teal-600 text-white text-xs rounded-full px-1.5 py-0.5 shrink-0">{summary.unread}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5 flex gap-3">
                        {summary.sent > 0 && <span>{summary.sent} gönderildi</span>}
                        {summary.received > 0 && <span>{summary.received} alındı</span>}
                        {!summary.sent && !summary.received && <span>Mailleşme yok</span>}
                      </div>
                    </button>
                  );
                })}
              </ScrollArea>
            </div>

            {/* Sağ: E-posta Thread */}
            <div className="flex-1 border border-gray-200 rounded-xl overflow-hidden flex flex-col">
              {!selectedMailCompany ? (
                <div className="flex-1 flex items-center justify-center text-center p-8">
                  <div>
                    <Inbox className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">Soldaki listeden bir firma seçin</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-700">{selectedMailCompany.name} — Mailleşmeler</p>
                    <Button size="sm" variant="outline" onClick={() => setEmailTarget(selectedMailCompany)}
                      className="gap-1.5 h-7 text-xs" data-testid="new-email-btn">
                      <Send className="w-3.5 h-3.5" /> Yeni Mail
                    </Button>
                  </div>

                  {/* Filter tabs: Tümü + her fuar (exhibition name) */}
                  {mailContext && (mailContext.contracts.length > 0 || mailContext.stands.length > 0) && (() => {
                    // Unread count helper
                    const unreadFor = (key) => {
                      if (key === 'all') return companyEmails.filter(e => e.direction === 'received' && !e.is_read).length;
                      if (key.startsWith('contract:')) {
                        const id = key.replace('contract:', '');
                        return companyEmails.filter(e => e.direction === 'received' && !e.is_read && e.contract_id === id).length;
                      }
                      if (key.startsWith('stand:')) {
                        const id = key.replace('stand:', '');
                        return companyEmails.filter(e => e.direction === 'received' && !e.is_read && e.stand_id === id).length;
                      }
                      return 0;
                    };
                    const allUnread = unreadFor('all');
                    return (
                      <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-1.5 flex-wrap bg-white">
                        {/* Tümü */}
                        <button
                          onClick={() => setMailFilter('all')}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1 ${mailFilter === 'all' ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-600 hover:border-teal-300'}`}
                          data-testid="mail-filter-all"
                        >
                          Tümü
                          {allUnread > 0 && <span className={`text-xs rounded-full px-1.5 py-0 font-semibold ${mailFilter === 'all' ? 'bg-white text-teal-700' : 'bg-teal-600 text-white'}`}>{allUnread}</span>}
                        </button>

                        {/* Sözleşme tabları — exhibition_name göster */}
                        {(mailContext.contracts || []).map(c => {
                          const key = `contract:${c.id}`;
                          const label = c.exhibition_name || c.template_name || 'Sözleşme';
                          const unread = unreadFor(key);
                          return (
                            <button
                              key={key}
                              onClick={() => setMailFilter(mailFilter === key ? 'all' : key)}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1 ${mailFilter === key ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-600 hover:border-teal-300'}`}
                              data-testid={`mail-filter-contract-${c.id}`}
                            >
                              <FileText className="w-3 h-3 shrink-0" />
                              {label}
                              {unread > 0 && <span className={`text-xs rounded-full px-1.5 py-0 font-semibold ${mailFilter === key ? 'bg-white text-teal-700' : 'bg-teal-600 text-white'}`}>{unread}</span>}
                            </button>
                          );
                        })}

                        {/* Stand tabları — exhibition_name göster */}
                        {(mailContext.stands || []).map(s => {
                          const key = `stand:${s.stand_id}`;
                          const label = s.exhibition_name || s.stand_number || 'Stand';
                          const unread = unreadFor(key);
                          return (
                            <button
                              key={key}
                              onClick={() => setMailFilter(mailFilter === key ? 'all' : key)}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1 ${mailFilter === key ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-300'}`}
                              data-testid={`mail-filter-stand-${s.stand_id}`}
                            >
                              <Building2 className="w-3 h-3 shrink-0" />
                              {label}
                              {unread > 0 && <span className={`text-xs rounded-full px-1.5 py-0 font-semibold ${mailFilter === key ? 'bg-white text-orange-600' : 'bg-orange-500 text-white'}`}>{unread}</span>}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}

                  <ScrollArea className="flex-1">
                    {mailsLoading ? (
                      <div className="text-center py-8 text-gray-400 text-sm">Yükleniyor...</div>
                    ) : (() => {
                      // Filter emails based on selected filter
                      let filtered = companyEmails;
                      if (mailFilter.startsWith('contract:')) {
                        const contractId = mailFilter.replace('contract:', '');
                        filtered = companyEmails.filter(e => e.contract_id === contractId);
                      } else if (mailFilter.startsWith('stand:')) {
                        const standId = mailFilter.replace('stand:', '');
                        filtered = companyEmails.filter(e => e.stand_id === standId);
                      }

                      const received = filtered.filter(e => e.direction === 'received').sort((a, b) => (b.received_at || '') > (a.received_at || '') ? 1 : -1);
                      const sent = filtered.filter(e => e.direction === 'sent').sort((a, b) => (b.sent_at || '') > (a.sent_at || '') ? 1 : -1);

                      if (received.length === 0 && sent.length === 0) return (
                        <div className="text-center py-8 text-gray-400 text-sm">
                          {mailFilter === 'all' ? 'Henüz mailleşme yok' : 'Bu fuara ait mail yok'}
                        </div>
                      );

                      // Render an email row
                      const renderEmail = (em) => {
                        const isSent = em.direction === 'sent';
                        const isExpanded = expandedEmail === em.id;
                        const isReplying = replyTarget?.sourceId === em.id;
                        const dateStr = (em.sent_at || em.received_at || '').slice(0, 16).replace('T', ' ');
                        return (
                          <div key={em.id}
                            className={`transition-colors ${!isSent && !em.is_read ? 'bg-blue-50/40' : ''}`}
                            data-testid={`email-item-${em.id}`}
                          >
                            {/* Header row — clickable */}
                            <div
                              className="px-4 py-3 cursor-pointer hover:bg-gray-50"
                              onClick={() => {
                                setExpandedEmail(isExpanded ? null : em.id);
                                if (isExpanded) setReplyTarget(null);
                                if (!isSent && !em.is_read) {
                                  axios.put(`${API}/emails/received/${em.id}/read`, {}, {
                                    headers: { Authorization: `Bearer ${token()}` }
                                  }).catch(() => {});
                                  setCompanyEmails(prev => prev.map(e => e.id === em.id ? { ...e, is_read: true } : e));
                                }
                              }}
                            >
                              <div className="flex items-center gap-2">
                                {!isSent && !em.is_read && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />}
                                <span className="text-sm font-medium text-gray-800 truncate flex-1">{em.subject}</span>
                                {em.has_attachment && <Paperclip className="w-3 h-3 text-gray-400 shrink-0" />}
                                <span className="text-xs text-gray-400 shrink-0">{dateStr}</span>
                                {isExpanded ? <ChevronLeft className="w-3.5 h-3.5 rotate-90 text-gray-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                              </div>
                              {!isSent && (
                                <p className="text-xs text-gray-500 mt-0.5 ml-4">
                                  {em.from_name ? `${em.from_name} <${em.from_email}>` : em.from_email}
                                </p>
                              )}
                            </div>

                            {/* Expanded body */}
                            {isExpanded && (
                              <div className="px-4 pb-3 border-t border-gray-100">
                                <div className="mt-2 pl-3 border-l-2 border-gray-200">
                                  {isSent ? (
                                    <>
                                      <p className="text-xs text-gray-500 mb-1"><strong>Kime:</strong> {(em.to_emails || []).join(', ')}</p>
                                      {em.cc_emails?.length > 0 && <p className="text-xs text-gray-500 mb-1"><strong>CC:</strong> {em.cc_emails.join(', ')}</p>}
                                      <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{em.body}</p>
                                    </>
                                  ) : (
                                    <>
                                      <div className="text-sm text-gray-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: em.body_text }} />
                                      {/* Yanıtla button */}
                                      <div className="mt-3 pt-2 border-t border-gray-100">
                                        {!isReplying ? (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setReplyTarget({
                                                sourceId: em.id,
                                                toEmail: em.from_email,
                                                toName: em.from_name,
                                                subject: em.subject,
                                                messageId: em.message_id,
                                                contractId: em.contract_id,
                                                standId: em.stand_id,
                                              });
                                              setReplyBody('');
                                            }}
                                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200 transition-colors font-medium"
                                            data-testid={`reply-btn-${em.id}`}
                                          >
                                            <ArrowUp className="w-3 h-3 rotate-180" />
                                            Yanıtla
                                          </button>
                                        ) : (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); setReplyTarget(null); setReplyBody(''); }}
                                            className="text-xs text-gray-400 hover:text-gray-600"
                                          >
                                            Yanıtı kapat
                                          </button>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>

                                {/* Inline reply composer */}
                                {isReplying && (
                                  <div
                                    className="mt-3 rounded-xl border border-teal-200 bg-teal-50/30 overflow-hidden"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <div className="flex items-center justify-between px-3 py-2 bg-teal-50 border-b border-teal-100">
                                      <div className="flex items-center gap-2">
                                        <Send className="w-3.5 h-3.5 text-teal-600" />
                                        <span className="text-xs font-semibold text-teal-700">Yanıt yaz</span>
                                        <span className="text-xs text-teal-500">→ {replyTarget.toName || replyTarget.toEmail}</span>
                                      </div>
                                      <button
                                        onClick={() => { setReplyTarget(null); setReplyBody(''); setReplyFiles([]); }}
                                        className="text-teal-400 hover:text-teal-700"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                    <div className="p-3 space-y-2">
                                      <textarea
                                        value={replyBody}
                                        onChange={e => setReplyBody(e.target.value)}
                                        placeholder="Yanıtınızı yazın..."
                                        rows={4}
                                        className="w-full text-sm border border-gray-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white"
                                        data-testid="reply-body-input"
                                        autoFocus
                                      />
                                      {/* Reply attachments */}
                                      <input
                                        ref={replyFileInputRef}
                                        type="file"
                                        multiple
                                        className="hidden"
                                        onChange={handleReplyFileAdd}
                                        data-testid="reply-file-input"
                                      />
                                      {replyFiles.length > 0 && (
                                        <div className="space-y-1">
                                          {replyFiles.map((f, i) => (
                                            <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-2 py-1 text-xs">
                                              <FileText className="w-3 h-3 text-gray-400 shrink-0" />
                                              <span className="flex-1 truncate text-gray-700">{f.name}</span>
                                              <span className="text-gray-400 shrink-0">{f.size < 1024*1024 ? `${(f.size/1024).toFixed(0)}KB` : `${(f.size/1024/1024).toFixed(1)}MB`}</span>
                                              <button onClick={() => setReplyFiles(prev => prev.filter((_,j) => j !== i))} className="text-gray-400 hover:text-red-500">
                                                <X className="w-3 h-3" />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      <div className="flex items-center justify-between">
                                        <button
                                          onClick={() => replyFileInputRef.current?.click()}
                                          disabled={replyFiles.length >= REPLY_MAX_FILES}
                                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-teal-600 disabled:opacity-40"
                                          data-testid="reply-add-file-btn"
                                        >
                                          <Paperclip className="w-3.5 h-3.5" /> Dosya Ekle
                                          {replyFiles.length > 0 && <span className="text-gray-400">({replyFiles.length}/{REPLY_MAX_FILES})</span>}
                                        </button>
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => { setReplyTarget(null); setReplyBody(''); setReplyFiles([]); }}
                                            className="text-xs px-3 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
                                          >
                                            İptal
                                          </button>
                                          <button
                                            onClick={sendReply}
                                            disabled={replySending || !replyBody.trim()}
                                            className="text-xs px-3 py-1.5 rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5 font-medium"
                                            data-testid="reply-send-btn"
                                          >
                                            <Send className="w-3 h-3" />
                                            {replySending ? 'Gönderiliyor...' : 'Gönder'}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      };

                      return (
                        <div>
                          {/* GELEN KUTUSU section */}
                          {received.length > 0 && (
                            <div>
                              <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
                                <ArrowDown className="w-3.5 h-3.5 text-blue-500" />
                                <span className="text-xs font-semibold text-blue-700">Gelen Kutusu</span>
                                <span className="text-xs text-blue-500">{received.length} mail</span>
                                {received.filter(e => !e.is_read).length > 0 && (
                                  <span className="ml-auto bg-blue-600 text-white text-xs rounded-full px-1.5 py-0.5 font-semibold">
                                    {received.filter(e => !e.is_read).length} okunmamış
                                  </span>
                                )}
                              </div>
                              <div className="divide-y divide-gray-100">
                                {received.map(renderEmail)}
                              </div>
                            </div>
                          )}

                          {/* GÖNDERİLENLER section */}
                          {sent.length > 0 && (
                            <div>
                              <div className="px-4 py-2 bg-teal-50 border-b border-teal-100 flex items-center gap-2">
                                <ArrowUp className="w-3.5 h-3.5 text-teal-600" />
                                <span className="text-xs font-semibold text-teal-700">Gönderilenler</span>
                                <span className="text-xs text-teal-500">{sent.length} mail</span>
                              </div>
                              <div className="divide-y divide-gray-100">
                                {sent.map(renderEmail)}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </ScrollArea>
                </>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) { setEditingCompany(null); setFormData(EMPTY_FORM); } }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Firmayı Düzenle' : 'Yeni Firma Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Tabs defaultValue="general">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="general" className="gap-2">
                  <Building2 className="w-4 h-4" /> Genel Bilgiler
                </TabsTrigger>
                <TabsTrigger value="contacts" className="gap-2">
                  <User className="w-4 h-4" /> İletişim Kişileri
                  {formData.contacts.length > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">{formData.contacts.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* General Info Tab */}
              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Firma Adı (Kısa) *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="ÖrnekFirma"
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Firma Ünvanı (Tam)</Label>
                    <Input
                      value={formData.full_name || ''}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Örnek Firma San. ve Tic. A.Ş."
                      data-testid="company-full-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>E-posta</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="firma@email.com"
                      data-testid="company-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefon</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+90 532 000 0000"
                      data-testid="company-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Adres</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Sokak, Mahalle"
                    data-testid="company-address-input"
                  />
                </div>

                {/* Location: Country, City, Zip */}
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-gray-400" /> Ülke
                    </Label>
                    <CountrySelect
                      value={formData.country}
                      onChange={(val) => setFormData({ ...formData, country: val })}
                      testId="company-country-select"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Şehir</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: toTitleCase(e.target.value) })}
                        placeholder="İstanbul"
                        data-testid="company-city-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Posta Kodu</Label>
                      <Input
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        placeholder="34000"
                        data-testid="company-zip-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Financial Info */}
                <div className="border-t pt-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-teal-600" /> Vergi Bilgileri
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Vergi Dairesi</Label>
                      <Input
                        value={formData.tax_office}
                        onChange={(e) => setFormData({ ...formData, tax_office: e.target.value })}
                        placeholder="Vergi dairesi adı"
                        data-testid="company-tax-office-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Vergi No</Label>
                      <Input
                        value={formData.tax_number}
                        onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                        placeholder="1234567890"
                        data-testid="company-tax-number-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notlar</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="İsteğe bağlı notlar..."
                    rows={2}
                    data-testid="company-notes-input"
                  />
                </div>
              </TabsContent>

              {/* Contacts Tab */}
              <TabsContent value="contacts" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">Firma ile ilişkili kişileri ekleyin</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    onClick={() => setFormData({
                      ...formData,
                      contacts: [...formData.contacts, { ...EMPTY_CONTACT, id: uuidv4() }]
                    })}
                    data-testid="add-contact-btn"
                  >
                    <UserPlus className="w-4 h-4" /> Kişi Ekle
                  </Button>
                </div>

                {formData.contacts.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center text-gray-400">
                    <User className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Henüz iletişim kişisi eklenmedi</p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-64">
                    <div className="space-y-3">
                      {formData.contacts.map((contact, idx) => (
                        <Card key={contact.id || idx} className="p-3 border border-gray-200">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-gray-500">Kişi #{idx + 1}</span>
                              <Button
                                type="button" size="icon" variant="ghost"
                                className="h-6 w-6 text-red-400 hover:text-red-600"
                                onClick={() => setFormData({ ...formData, contacts: formData.contacts.filter((_, i) => i !== idx) })}
                                data-testid={`remove-contact-${idx}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>

                            {/* Name + Title */}
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs">Ad Soyad *</Label>
                                <Input
                                  value={contact.name}
                                  onChange={(e) => {
                                    const updated = [...formData.contacts];
                                    updated[idx] = { ...updated[idx], name: e.target.value };
                                    setFormData({ ...formData, contacts: updated });
                                  }}
                                  placeholder="Ad Soyad"
                                  className="h-8 text-sm"
                                  data-testid={`contact-name-${idx}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Unvan</Label>
                                <Input
                                  value={contact.title}
                                  onChange={(e) => {
                                    const updated = [...formData.contacts];
                                    updated[idx] = { ...updated[idx], title: e.target.value };
                                    setFormData({ ...formData, contacts: updated });
                                  }}
                                  placeholder="Direktör, Muhasebe..."
                                  className="h-8 text-sm"
                                  data-testid={`contact-title-${idx}`}
                                />
                              </div>
                            </div>

                            {/* Email */}
                            <div className="space-y-1">
                              <Label className="text-xs">E-posta</Label>
                              <Input
                                type="email"
                                value={contact.email}
                                onChange={(e) => {
                                  const updated = [...formData.contacts];
                                  updated[idx] = { ...updated[idx], email: e.target.value };
                                  setFormData({ ...formData, contacts: updated });
                                }}
                                placeholder="kisi@firma.com"
                                className="h-8 text-sm"
                                data-testid={`contact-email-${idx}`}
                              />
                            </div>

                            {/* Phone: country code + number */}
                            <div className="space-y-1">
                              <Label className="text-xs">Telefon</Label>
                              <div className="flex gap-2">
                                <PhoneCodeSelect
                                  value={contact.phone_country_code || '+90'}
                                  onChange={(code) => {
                                    const updated = [...formData.contacts];
                                    updated[idx] = { ...updated[idx], phone_country_code: code };
                                    setFormData({ ...formData, contacts: updated });
                                  }}
                                  testId={`contact-phone-code-${idx}`}
                                />
                                <Input
                                  value={contact.phone_number || ''}
                                  onChange={(e) => {
                                    const updated = [...formData.contacts];
                                    updated[idx] = { ...updated[idx], phone_number: e.target.value };
                                    setFormData({ ...formData, contacts: updated });
                                  }}
                                  placeholder="532 000 0000"
                                  className="h-8 text-sm flex-1"
                                  data-testid={`contact-phone-${idx}`}
                                />
                              </div>
                            </div>

                            {/* Country + City + Zip */}
                            <div className="space-y-1">
                              <Label className="text-xs flex items-center gap-1">
                                <Globe className="w-3 h-3 text-gray-400" /> Ülke
                              </Label>
                              <CountrySelect
                                value={contact.country || ''}
                                onChange={(val) => {
                                  const updated = [...formData.contacts];
                                  updated[idx] = { ...updated[idx], country: val };
                                  setFormData({ ...formData, contacts: updated });
                                }}
                                placeholder="Ülke seçin..."
                                testId={`contact-country-${idx}`}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs">Şehir</Label>
                                <Input
                                  value={contact.city || ''}
                                  onChange={(e) => {
                                    const updated = [...formData.contacts];
                                    updated[idx] = { ...updated[idx], city: toTitleCase(e.target.value) };
                                    setFormData({ ...formData, contacts: updated });
                                  }}
                                  placeholder="İstanbul"
                                  className="h-8 text-sm"
                                  data-testid={`contact-city-${idx}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Posta Kodu</Label>
                                <Input
                                  value={contact.zip_code || ''}
                                  onChange={(e) => {
                                    const updated = [...formData.contacts];
                                    updated[idx] = { ...updated[idx], zip_code: e.target.value };
                                    setFormData({ ...formData, contacts: updated });
                                  }}
                                  placeholder="34000"
                                  className="h-8 text-sm"
                                  data-testid={`contact-zip-${idx}`}
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`signatory-${idx}`}
                                checked={contact.is_signatory}
                                onCheckedChange={(checked) => {
                                  const updated = [...formData.contacts];
                                  updated[idx] = { ...updated[idx], is_signatory: !!checked };
                                  setFormData({ ...formData, contacts: updated });
                                }}
                                data-testid={`contact-signatory-${idx}`}
                              />
                              <Label htmlFor={`signatory-${idx}`} className="text-xs cursor-pointer">
                                İmza yetkilisi
                              </Label>
                              {contact.is_signatory && (
                                <Badge variant="secondary" className="text-xs">Yetkili</Badge>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>İptal</Button>
              <Button type="submit" data-testid="save-company-btn">
                {editingCompany ? 'Güncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Company Preview Modal */}
      <Dialog open={!!previewCompany} onOpenChange={(o) => !o && setPreviewCompany(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-600" />
              {previewCompany?.name}
            </DialogTitle>
          </DialogHeader>
          {previewCompany && (
            <div className="space-y-4 text-sm">
              {/* Status */}
              <div className="flex items-center gap-2">
                <StatusBadge company={previewCompany} />
                {previewCompany.full_name && (
                  <span className="text-gray-500 text-xs">{previewCompany.full_name}</span>
                )}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-3">
                {previewCompany.email && (
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400">E-posta</p>
                      <p className="font-medium">{previewCompany.email}</p>
                    </div>
                  </div>
                )}
                {previewCompany.phone && (
                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400">Telefon</p>
                      <p className="font-medium">{previewCompany.phone}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Address */}
              {(previewCompany.address || previewCompany.city || previewCompany.country) && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400">Adres</p>
                    <p className="font-medium">
                      {[previewCompany.address, previewCompany.city, previewCompany.country].filter(Boolean).join(', ')}
                      {previewCompany.zip_code && ` ${previewCompany.zip_code}`}
                    </p>
                  </div>
                </div>
              )}

              {/* Tax Info */}
              {(previewCompany.tax_office || previewCompany.tax_number) && (
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <Receipt className="w-3.5 h-3.5" /> Vergi Bilgileri
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {previewCompany.tax_office && (
                      <div>
                        <p className="text-xs text-gray-400">Vergi Dairesi</p>
                        <p className="font-medium">{previewCompany.tax_office}</p>
                      </div>
                    )}
                    {previewCompany.tax_number && (
                      <div>
                        <p className="text-xs text-gray-400">Vergi No</p>
                        <p className="font-medium">{previewCompany.tax_number}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contacts */}
              {previewCompany.contacts && previewCompany.contacts.length > 0 && (
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <User className="w-3.5 h-3.5" /> İletişim Kişileri ({previewCompany.contacts.length})
                  </p>
                  {previewCompany.contacts.map((c, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800">{c.name}</p>
                        {c.title && <span className="text-xs text-gray-500">— {c.title}</span>}
                        {c.is_signatory && (
                          <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">Yetkili</span>
                        )}
                      </div>
                      {c.email && <p className="text-xs text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</p>}
                      {(c.phone_number || c.phone) && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {c.phone_country_code || ''} {c.phone_number || c.phone}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Notes */}
              {previewCompany.notes && (
                <div className="border-t pt-3">
                  <p className="text-xs text-gray-400 mb-1">Notlar</p>
                  <p className="text-gray-700 text-sm italic">{previewCompany.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setPreviewCompany(null)}>Kapat</Button>
            <Button size="sm" onClick={() => { openEdit(previewCompany); setPreviewCompany(null); }}>
              <Edit className="w-4 h-4 mr-1" /> Düzenle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Firmayı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name}</strong> firmasını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Blacklist Dialog */}
      <Dialog open={!!blacklistTarget} onOpenChange={(o) => !o && setBlacklistTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {blacklistTarget?.is_blacklisted
                ? <><ShieldCheck className="w-5 h-5 text-green-600" /> Kara Listeden Çıkar</>
                : <><AlertTriangle className="w-5 h-5 text-orange-500" /> Kara Listeye Al</>
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {blacklistTarget?.is_blacklisted
                ? `"${blacklistTarget?.name}" firmasını kara listeden çıkarmak istiyor musunuz?`
                : `"${blacklistTarget?.name}" firmasını kara listeye almak istiyor musunuz?`
              }
            </p>
            {!blacklistTarget?.is_blacklisted && (
              <div className="space-y-1">
                <Label>Neden (isteğe bağlı)</Label>
                <Textarea
                  value={blacklistReason}
                  onChange={(e) => setBlacklistReason(e.target.value)}
                  placeholder="Kara listeye alınma nedeni..."
                  rows={2}
                  data-testid="blacklist-reason-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlacklistTarget(null)}>İptal</Button>
            <Button
              onClick={handleBlacklist}
              className={blacklistTarget?.is_blacklisted ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-500 hover:bg-orange-600'}
              data-testid="confirm-blacklist-btn"
            >
              {blacklistTarget?.is_blacklisted ? 'Kara Listeden Çıkar' : 'Kara Listeye Al'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Modal */}
      <SendEmailModal
        open={!!emailTarget}
        onClose={() => setEmailTarget(null)}
        companyId={emailTarget?.id}
        companyName={emailTarget?.name}
      />

      {/* Create Contract Modal */}
      <Dialog open={!!createContractCompany} onOpenChange={(o) => !o && closeContractModal()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              {contractModalStep === 'success'
                ? 'Sözleşme Oluşturuldu'
                : `Sözleşme Oluştur — ${createContractCompany?.name}`}
            </DialogTitle>
          </DialogHeader>

          {/* Step indicator */}
          {contractModalStep !== 'success' && (
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
              <span className={`flex items-center gap-1 ${contractModalStep === 1 ? 'text-teal-600 font-semibold' : 'text-gray-400'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${contractModalStep === 1 ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'}`}>1</span>
                Atama Seç
              </span>
              <ChevronRight className="w-3 h-3" />
              <span className={`flex items-center gap-1 ${contractModalStep === 2 ? 'text-teal-600 font-semibold' : 'text-gray-400'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${contractModalStep === 2 ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'}`}>2</span>
                Şablon Seç
              </span>
            </div>
          )}

          {contractModalLoading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Yükleniyor...</div>
          ) : contractModalStep === 1 ? (
            /* ── Step 1: Select Assignment ── */
            <div className="space-y-2" data-testid="contract-step-1">
              {contractAssignments.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">Sözleşmesiz atama bulunamadı.</p>
              ) : (
                <ScrollArea className="max-h-72">
                  <div className="space-y-2 pr-1">
                    {contractAssignments.map((a) => (
                      <div
                        key={a.stand_id}
                        onClick={() => setSelectedAssignment(a)}
                        data-testid={`assignment-item-${a.stand_id}`}
                        className={`cursor-pointer rounded-lg border p-3 transition-all ${
                          selectedAssignment?.stand_id === a.stand_id
                            ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-400'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-sm text-gray-800">{a.exhibition_name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Stand: <span className="font-medium">{a.stand_number}</span>
                              <span className="mx-1.5 text-gray-300">·</span>
                              {a.area} m²
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              a.status === 'satıldı' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                              {a.status_label}
                            </span>
                            {selectedAssignment?.stand_id === a.stand_id && (
                              <Check className="w-4 h-4 text-teal-600" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          ) : contractModalStep === 2 ? (
            /* ── Step 2: Select Template ── */
            <div className="space-y-2" data-testid="contract-step-2">
              {contractTemplates.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">Henüz şablon oluşturulmamış.</p>
              ) : (
                <ScrollArea className="max-h-72">
                  <div className="space-y-2 pr-1">
                    {contractTemplates.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => setSelectedTemplate(t)}
                        data-testid={`template-item-${t.id}`}
                        className={`cursor-pointer rounded-lg border p-3 transition-all ${
                          selectedTemplate?.id === t.id
                            ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-400'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-sm text-gray-800">{t.name}</p>
                            {t.description && (
                              <p className="text-xs text-gray-400 mt-0.5">{t.description}</p>
                            )}
                          </div>
                          {selectedTemplate?.id === t.id && (
                            <Check className="w-4 h-4 text-teal-600 shrink-0" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          ) : (
            /* ── Success ── */
            <div className="py-6 text-center space-y-3" data-testid="contract-success">
              <CheckCircle2 className="w-14 h-14 text-teal-500 mx-auto" />
              <p className="font-semibold text-gray-800">Sözleşme başarıyla oluşturuldu!</p>
              <p className="text-sm text-gray-500">
                <strong>{createContractCompany?.name}</strong> firması için sözleşme oluşturuldu.
              </p>
            </div>
          )}

          {/* Footer */}
          <DialogFooter className="gap-2 mt-2">
            {contractModalStep === 1 && (
              <>
                <Button variant="outline" onClick={closeContractModal} data-testid="contract-modal-cancel">İptal</Button>
                <Button
                  onClick={() => setContractModalStep(2)}
                  disabled={!selectedAssignment}
                  className="gap-1"
                  data-testid="contract-step1-next"
                >
                  İleri <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )}
            {contractModalStep === 2 && (
              <>
                <Button variant="outline" onClick={() => setContractModalStep(1)} className="gap-1" data-testid="contract-step2-back">
                  <ChevronLeft className="w-4 h-4" /> Geri
                </Button>
                <Button
                  onClick={handleCreateContract}
                  disabled={!selectedTemplate || contractModalLoading}
                  className="bg-teal-600 hover:bg-teal-700 gap-1"
                  data-testid="contract-create-btn"
                >
                  {contractModalLoading ? 'Oluşturuluyor...' : 'Oluştur'}
                </Button>
              </>
            )}
            {contractModalStep === 'success' && (
              <>
                <Button variant="outline" onClick={closeContractModal} data-testid="contract-close-btn">Kapat</Button>
                <Button
                  onClick={() => { closeContractModal(); navigate('/organizer/contracts'); }}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="go-to-contracts-btn"
                >
                  Sözleşmelere Git
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
